import { useState } from 'react';
import { declareTaxableIncomeSchema } from '@brighte/validation';
import { PropertyTitleOwnerOutput } from '@graphql/index';
import { Accordion, AccordionItem } from '@spark-web/accordion';
import { Box } from '@spark-web/box';
import { Button } from '@spark-web/button';
import { CurrencyInput } from '@spark-web/currency-input';
import { Field } from '@spark-web/field';
import { Heading } from '@spark-web/heading';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import EligibilityStepHeader from './EligibilityStepHeader';

type DeclareTaxableIncomeProps = {
  owners: PropertyTitleOwnerOutput[];
  productName: string;
  initialIncomes?: Record<string, string>;
  onConfirm: (incomes: Record<string, string>) => void;
  onBack: () => void;
};

function getInitials(fullName: string): string {
  return fullName
    .split(' ')
    .slice(0, 2)
    .map(word => word[0]?.toUpperCase() ?? '')
    .join('');
}

const AVATAR_BACKGROUNDS: Array<'primaryLow' | 'infoLow'> = ['primaryLow', 'infoLow'];

function DeclareTaxableIncome({ owners, productName, initialIncomes, onConfirm, onBack }: DeclareTaxableIncomeProps) {
  const [incomes, setIncomes] = useState<Record<string, number>>(() =>
    Object.fromEntries(Object.entries(initialIncomes ?? {}).map(([id, val]) => [id, Number(val)])),
  );
  const [incomeErrors, setIncomeErrors] = useState<Record<string, string>>({});
  // Tracks whether each field has been touched. Mirrors CurrencyInputField's isPristine
  // logic so that non-numeric input is rejected and the display stays numeric-only.
  const [touchedMap, setTouchedMap] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(Object.keys(initialIncomes ?? {}).map(id => [id, true])),
  );

  const handleIncomeChange = (ownerId: string, value: number | string) => {
    const numericValue = typeof value === 'string' ? 0 : value;
    setTouchedMap(prev => ({ ...prev, [ownerId]: true }));
    setIncomes(prev => ({ ...prev, [ownerId]: numericValue }));
    setIncomeErrors(prev => ({ ...prev, [ownerId]: '' }));
  };

  const handleConfirm = () => {
    const untouchedErrors: Record<string, string> = {};
    for (const owner of owners) {
      if (!touchedMap[owner.id]) {
        untouchedErrors[owner.id] = 'Annual taxable income is a required field';
      }
    }
    if (Object.keys(untouchedErrors).length > 0) {
      setIncomeErrors(untouchedErrors);
      return;
    }

    const completeIncomes = Object.fromEntries(owners.map(owner => [owner.id, incomes[owner.id] ?? 0]));
    const result = declareTaxableIncomeSchema.safeParse(completeIncomes);

    if (!result.success) {
      const formatted = result.error.format();
      const errors: Record<string, string> = {};
      for (const owner of owners) {
        const fieldErrors = (formatted as Record<string, { _errors: string[] }>)[owner.id]?._errors;
        if (fieldErrors?.length) {
          errors[owner.id] = fieldErrors[0];
        }
      }
      setIncomeErrors(errors);
      return;
    }

    setIncomeErrors({});
    onConfirm(Object.fromEntries(Object.entries(result.data).map(([id, amount]) => [id, String(amount)])));
  };

  return (
    <Stack padding="xlarge" gap="xlarge">
      <EligibilityStepHeader currentStep={4} totalSteps={5} productName={productName} />

      <Stack gap="large">
        <Heading level="1">Declare taxable income</Heading>

        {owners.map((owner, index) => (
          <Box key={owner.id} borderRadius="medium" padding="large" borderWidth="standard" border="standard">
            <Stack gap="medium">
              <Box
                borderRadius="full"
                background={AVATAR_BACKGROUNDS[index] ?? 'infoLow'}
                display="flex"
                alignItems="center"
                justifyContent="center"
                style={{ width: 40, height: 40 }}
              >
                <Text weight="semibold" size="small">
                  {getInitials(owner.fullName)}
                </Text>
              </Box>

              <Text weight="semibold">{owner.fullName}</Text>

              <Box marginTop="small">
                <Field
                  label="Annual taxable income"
                  tone={incomeErrors[owner.id] ? 'critical' : undefined}
                  message={incomeErrors[owner.id] || undefined}
                >
                  <CurrencyInput
                    value={touchedMap[owner.id] ? (incomes[owner.id] ?? '') : ''}
                    onChange={value => handleIncomeChange(owner.id, value)}
                  />
                </Field>
              </Box>
            </Stack>
          </Box>
        ))}

        <Accordion type="multiple">
          <AccordionItem value="q1" label="Why do I have to declare my income?" level="4">
            <Text>
              The NSW Government caps household income at $210,000 after tax for this program. We need to confirm the
              combined income of everyone on the property title to check your household qualifies. The figure you enter
              here is self-declared and is only used for this eligibility check - it won&apos;t be used to assess your
              loan application.
            </Text>
          </AccordionItem>
          <AccordionItem value="q2" label="What can I do if my income is above $210,000?" level="4">
            <Text>
              Unfortunately your household won&apos;t be eligible for this program. Contact your vendor - they can help
              you explore other Brighte finance options for your home upgrade.
            </Text>
          </AccordionItem>
        </Accordion>

        <Button tone="primary" onClick={handleConfirm}>
          Confirm
        </Button>

        <Box alignSelf="start">
          <Button prominence="none" tone="neutral" onClick={onBack}>
            ← Back
          </Button>
        </Box>
      </Stack>
    </Stack>
  );
}

export default DeclareTaxableIncome;
